import { useEffect, useRef, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { onWs } from "../api/ws.js";
import { toast } from "../ui/Toast.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { Timer } from "../ui/Timer.js";
import { useBalances } from "../state/balances.js";
import { useSession } from "../state/session.js";
import { STORE_TABS, useStore, type StoreTab } from "./state.js";
import { OfferCard, PurchaseFx, PurchaseSheet } from "./cards.js";
import { FuelOffers, OwnedFuel, isFuelCard } from "../world/FuelSheet.js";
import { storeApi } from "./api.js";
import { PassesView } from "./PassesView.js";
import { VipLounge } from "./VipLounge.js";
import { LuckyView } from "./LuckyView.js";
import { ShopsView } from "./ShopsView.js";
import { CosmeticsView } from "./CosmeticsView.js";
import type { StoreCard } from "./types.js";
import { catalogueExpiresAt } from "./catalogue.js";
import { serverNow } from "../state/serverTime.js";
import { useBase } from "../base/state.js";
import { useUnits } from "../units/state.js";
import { Icon } from "../ui/Icon.js";
import { AssetIcon } from "../assets/Icon.js";
import { OfferShowcase } from "./OfferShowcase.js";
import "./store.css";
import "./storeShowcase.css";

/** Subscribes the store to pushes for the whole session: flash sales, chain offers, purchases, VIP levels. */
export function useStorePushes(): void {
  const t = useT();
  const accountId = useSession((state) => state.account?.id);
  useEffect(() => {
    if (accountId) {
      if (!useStore.getState().cosmetics) void useStore.getState().refreshCosmetics();
    }
  }, [accountId]);
  useEffect(() => {
    const invalidate = () => {
      useStore.getState().invalidate();
      if (location.pathname.startsWith("/app/store")) void useStore.getState().refresh();
    };
    const offFlash = onWs("store.flash", () => {
      useStore.getState().noteFlash();
      invalidate();
    });
    const offOffer = onWs("store.offer", () => {
      useStore.getState().noteOffer();
      invalidate();
    });
    const offVip = onWs("vip.levelup", (e) => {
      const p = e.payload as { level: number; megaRank: number };
      toast.reward(
        p.megaRank > 0
          ? t("store.push.megavip", { rank: p.megaRank })
          : t("store.push.vip", { level: p.level }),
      );
      invalidate();
    });
    const offDoubles = onWs("store.doubles", () => {
      invalidate();
    });
    const offDay = onWs("day.new", invalidate);
    const cosmeticsChanged = () => {
      void useStore.getState().refreshCosmetics();
      void useBase.getState().refresh();
      void useUnits.getState().refreshRoster();
    };
    const offCosmeticEquip = onWs("cosmetic.equipped", cosmeticsChanged);
    const offCosmeticUnequip = onWs("cosmetic.unequipped", cosmeticsChanged);
    // VIP points move the HUD badge and the lounge: reload the session snapshot (debounced).
    let reload: number | null = null;
    const offPoints = onWs("vip.points", () => {
      if (reload !== null) window.clearTimeout(reload);
      reload = window.setTimeout(() => void useSession.getState().load(), 400);
    });
    return () => {
      offFlash();
      offOffer();
      offVip();
      offDoubles();
      offDay();
      offCosmeticEquip();
      offCosmeticUnequip();
      offPoints();
      if (reload !== null) window.clearTimeout(reload);
    };
  }, [t]);
}

function Grid({ cards, testId }: { cards: StoreCard[]; testId: string }) {
  return (
    <div className="offers" data-testid={testId}>
      {cards.map((c) => (
        <OfferCard key={c.key} card={c} />
      ))}
    </div>
  );
}

function Featured() {
  const t = useT();
  const view = useStore((s) => s.view);
  if (!view) return null;
  return <OfferShowcase title={t("store.redesign.spotlight")} testId="store-featured" cards={[...view.chains, ...view.flash, view.daily.mustBuy, ...(view.recommended ? [view.recommended] : []), view.daily.weekly, view.daily.monthly]} />;
}

function Packs() {
  const t = useT();
  const view = useStore((s) => s.view);
  if (!view) return null;
  return <><OfferShowcase title={t("store.redesign.packsTitle")} testId="store-packs" cards={view.packs} preferKey="pack.starter" /><p className="store-pack-note">{view.doublesRefreshed ? t("store.packs.refreshed") : t("store.packs.hint")}</p></>;
}

function Daily() {
  const t = useT();
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const view = useStore((s) => s.view);
  const run = useStore((s) => s.run);
  const refresh = useStore((s) => s.refresh);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  if (!view) return null;
  const reroll = async () => {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    try {
      const res = await run(() => storeApi.refreshDaily(), t("store.daily.rerolled"));
      if (res) { setConfirmRefresh(false); await refresh(true); }
    } finally { pending.current = false; setBusy(false); }
  };
  const free = view.daily.refresh.freeLeft > 0;
  return (
    <>
    <div className="daily-offer-tools">
      <span><Icon name="clock" size={14} /> {t("store.daily.resets")} <Timer finishesAt={view.daily.refresh.resetsAt} /></span>
        <Button
          variant={free ? "primary" : "default"}
          onClick={() => free ? void reroll() : setConfirmRefresh(true)}
          disabled={busy || (!free && credits < view.daily.refresh.price)}
          aria-busy={busy}
          data-testid="daily-refresh"
        >
          {free
            ? t("store.daily.refreshFree")
            : t("store.daily.refreshPaid", { n: view.daily.refresh.price })}
        </Button>
    </div>
    {confirmRefresh ? <section className="daily-refresh-confirm" data-testid="daily-refresh-confirm" aria-label={t("store.daily.refreshPaid", { n: view.daily.refresh.price })}>
      <p>{pick({ de: "Die fünf Tagesangebote werden neu zusammengestellt.", en: "Replace the five daily offers with a new selection." })}</p>
      <Button disabled={busy} onClick={() => setConfirmRefresh(false)}>{t("common.cancel")}</Button>
      <Button variant="gold" disabled={busy || (!free && credits < view.daily.refresh.price)} onClick={() => void reroll()} data-testid="daily-refresh-pay">{free ? t("store.daily.refreshFree") : t("store.daily.refreshPaid", { n: view.daily.refresh.price })}</Button>
    </section> : null}
    <OfferShowcase title={t("store.redesign.dailyTitle")} testId="store-daily" cards={[...view.daily.cards, view.daily.mustBuy, view.daily.weekly, view.daily.monthly]} />
    </>
  );
}

function Catalogue({ tab }: { tab: "speedups" | "resources" | "fabrication" }) {
  const t = useT();
  const view = useStore((s) => s.view);
  if (!view) return null;
  // Fuel gets its own section at the top of Supplies: owned refills first, then the three sizes (D65).
  const fuel = tab === "resources" ? view[tab].filter(isFuelCard) : [];
  return (
    <>
      {fuel.length > 0 ? (
        <Panel title={t("fuel.title")}>
          <div data-testid="store-fuel"><OwnedFuel /><FuelOffers cards={fuel} /></div>
        </Panel>
      ) : null}
      <Panel title={t(`store.tab.${tab}`)}>
        <Grid cards={view[tab].filter((c) => !isFuelCard(c))} testId={`store-${tab}`} />
      </Panel>
    </>
  );
}

/** `/app/store[/tab]`: the store with every tab from content plus VIP lounge, lucky and shops (60-store.md). */
export function StoreHub({ route }: { route: string }) {
  const t = useT();
  const view = useStore((s) => s.view);
  const refresh = useStore((s) => s.refresh);
  const error = useStore((s) => s.error);
  const seen = useStore((s) => s.seen);
  const flashCount = useStore((s) => s.flashCount);
  const offerCount = useStore((s) => s.offerCount);
  const seg = route.split("/")[3] as StoreTab | undefined;
  const tab: StoreTab = seg && STORE_TABS.includes(seg) ? seg : "featured";
  useEffect(() => {
    void refresh();
    seen();
  }, [refresh, seen]);
  useEffect(() => {
    if (!view) return;
    const wait = Math.max(1000, catalogueExpiresAt(view, serverNow()) - serverNow());
    const timer = window.setTimeout(() => void refresh(true), wait);
    return () => window.clearTimeout(timer);
  }, [view, refresh]);
  const badges: Partial<Record<StoreTab, number>> = {
    featured: flashCount + offerCount + (view?.chains.length ?? 0),
  };
  return (
    <div className="page store" data-testid="store-hub">
      <nav className="store-navigation" aria-label={t("store.redesign.more")}>
      <div className="store-primary-tabs">
        {(["featured", "packs", "daily", "vip"] as const).map((x) => (
          <button
            key={x}
            type="button"
            className={`tab ${x === tab ? "active" : ""}`}
            aria-current={x === tab ? "page" : undefined}
            onClick={() => navigate(x === "featured" ? "/app/store" : `/app/store/${x}`)}
            data-testid={`store-tab-${x}`}
          >
            <AssetIcon id={{ featured: "itm.chest_gold", packs: "unit.bulwark_titan", daily: "ui.res.credits", vip: "bld.vip_lounge" }[x]} size={36} alt="" />{t(`store.tab.${x}`)}
            {(badges[x] ?? 0) > 0 ? (
              <span className="badge gold" style={{ marginLeft: 6 }}>
                {badges[x]}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <details key={tab} className="store-more-categories">
        <summary data-testid="store-more-categories"><Icon name="menu" size={17} /><span>{t("store.redesign.more")}</span>{!["featured", "packs", "daily", "vip"].includes(tab) ? <b>{t(`store.tab.${tab}`)}</b> : null}<Icon name="chevronDown" size={16} /></summary>
        <div className="store-category-grid">{STORE_TABS.filter(x => !["featured", "packs", "daily", "vip"].includes(x)).map(x => <button key={x} type="button" className={`tab ${x === tab ? "active" : ""}`} aria-current={x === tab ? "page" : undefined} onClick={() => navigate(`/app/store/${x}`)} data-testid={`store-tab-${x}`}>{t(`store.tab.${x}`)}</button>)}</div>
      </details>
      </nav>
      {!view ? <p className="hint">{t("common.loading")}</p> : null}
      {error ? (
        <div className="glass" role="alert" style={{ padding: 12 }}>
          <p>{t("store.loadFailed")}</p>
          <Button onClick={() => void refresh(true)}>{t("store.retry")}</Button>
        </div>
      ) : null}
      {tab === "featured" ? <Featured /> : null}
      {tab === "packs" ? <Packs /> : null}
      {tab === "daily" ? <Daily /> : null}
      {tab === "passes" ? <PassesView /> : null}
      {tab === "speedups" || tab === "resources" || tab === "fabrication" ? <Catalogue tab={tab} /> : null}
      {tab === "vip" ? <VipLounge /> : null}
      {tab === "lucky" ? <LuckyView /> : null}
      {tab === "cosmetics" ? <CosmeticsView /> : null}
      {tab === "shops" ? <ShopsView /> : null}
      <PurchaseSheet />
      <PurchaseFx />
      {view ? (
        <span hidden data-testid="store-month">
          {view.monthKey}
        </span>
      ) : null}
      {view ? (
        <span hidden data-testid="store-vip">
          {pick({ de: `VIP ${view.vipLevel}`, en: `VIP ${view.vipLevel}` })}
        </span>
      ) : null}
    </div>
  );
}
